import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { getStoredUser } from "../utils/auth";
import "./NotificationsBell.css";
import { createSocket } from "../utils/socket";

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const user = useMemo(() => getStoredUser(), []);
  const userId = String(user?._id || user?.id || user?.userId || "");
  const socketRef = useRef(null);

  const unread = useMemo(
    () => items.filter((item) => !item.readAt && !item.read).length,
    [items]
  );

  useEffect(() => {
    let cancelled = false;
    if (!userId) return undefined;

    async function loadNotifications() {
      try {
        const res = await client.get("/api/notifications");
        if (cancelled) return;

        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.items)
          ? res.data.items
          : [];

        setItems(list);
      } catch (error) {
        if (error?.response?.status === 429) {
          console.warn("Notifications are being throttled; skipping repeated reloads.");
        }
      }
    }

    loadNotifications();

    const socket = createSocket();
    socketRef.current = socket;

    const onNotification = (notification) => {
      if (!notification) return;

      setItems((prev) =>
        [
          notification,
          ...prev.filter(
            (item) =>
              String(item?._id || item?.id || "") !==
              String(notification?._id || notification?.id || "")
          ),
        ].slice(0, 25)
      );
    };

    socket.on("notification", onNotification);
    socket.on("connect_error", () => {
      try {
        socket.disconnect();
      } catch {}
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        try {
          socketRef.current.off("notification", onNotification);
          socketRef.current.disconnect();
        } catch {}
      }
      socketRef.current = null;
    };
  }, [userId]);

  const markAllRead = async () => {
    if (!unread || markingAllRead) return;

    setMarkingAllRead(true);
    try {
      await client.post("/api/notifications/read-all");
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          read: true,
          readAt: item.readAt || new Date().toISOString(),
        }))
      );
    } catch (error) {
      console.error("Could not mark all notifications as read", error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const markOneRead = async (notificationId) => {
    if (!notificationId) return;

    const target = items.find(
      (item) => String(item?._id || item?.id || "") === String(notificationId)
    );
    if (!target || target.read || target.readAt) return;

    try {
      await client.post(`/api/notifications/${notificationId}/read`);
      setItems((prev) =>
        prev.map((item) =>
          String(item?._id || item?.id || "") === String(notificationId)
            ? {
                ...item,
                read: true,
                readAt: item.readAt || new Date().toISOString(),
              }
            : item
        )
      );
    } catch (error) {
      console.error("Could not mark notification as read", error);
    }
  };

  const handleToggleOpen = async () => {
    const next = !open;
    setOpen(next);

    if (next) {
      try {
        const res = await client.get("/api/notifications");
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.items)
          ? res.data.items
          : [];

        setItems(list);

        const hasUnread = list.some((item) => !item.readAt && !item.read);
        if (hasUnread) {
          await client.post("/api/notifications/read-all");
          setItems((prev) =>
            prev.map((item) => ({
              ...item,
              read: true,
              readAt: item.readAt || new Date().toISOString(),
            }))
          );
        }
      } catch (error) {
        console.error("Could not refresh notifications", error);
      }
    }
  };

  const handleNotificationClick = async (item) => {
    const notificationId = item?._id || item?.id || "";
    await markOneRead(notificationId);
    setOpen(false);

    const convoId = item?.meta?.conversationId;
    const entityType = item?.meta?.entityType;
    const entityId = item?.meta?.entityId;

    if (convoId) {
      navigate(`/messages?convoId=${encodeURIComponent(convoId)}`);
      return;
    }

    if (entityType && entityId) {
      navigate(`/${entityType}s/${entityId}`);
      return;
    }

    navigate("/my-listings");
  };

  if (!userId) return null;

  return (
    <div className="notifications">
      <button
        type="button"
        className="notifications__trigger"
        title={`${unread} unread notifications`}
        onClick={handleToggleOpen}
      >
        <FaBell />
        {unread > 0 ? <span className="notifications__badge">{unread}</span> : null}
      </button>

      {open ? (
        <div className="notifications__panel">
          <div className="notifications__header">
            <strong>Notifications</strong>
            <span>{unread} unread</span>
          </div>

          <div className="notifications__list">
            {items.length === 0 ? (
              <div className="notifications__empty">No notifications yet.</div>
            ) : (
              items.map((item, index) => {
                const isUnread = !item.readAt && !item.read;
                return (
                  <button
                    key={item._id || item.id || index}
                    type="button"
                    className={`notifications__item ${isUnread ? "is-unread" : ""}`}
                    onClick={() => handleNotificationClick(item)}
                  >
                    <div className="notifications__text">
                      {item.content || item.message || "New activity on your account."}
                    </div>
                    <div className="notifications__meta">
                      {item.type || "update"} ·{" "}
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString()
                        : "just now"}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {items.length > 0 && unread > 0 ? (
            <div
              style={{
                padding: "10px 12px",
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <button
                type="button"
                className="notifications__markall"
                onClick={markAllRead}
                disabled={markingAllRead}
                style={{
                  width: "100%",
                  minHeight: 40,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "inherit",
                  fontWeight: 700,
                  cursor: markingAllRead ? "not-allowed" : "pointer",
                  opacity: markingAllRead ? 0.6 : 1,
                }}
              >
                {markingAllRead ? "Marking as read..." : "Mark all as read"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}